import { useCallback, useEffect, useState } from 'react'
import { ROLES, capabilitiesFor, isRole } from '@qim/shared'
import { ApiError, fetchAccess, putAssignment, revokeAssignment } from '../api.js'
import { formatInstant } from '../format.js'
import type { Assignment } from '../api.js'
import type { Role } from '@qim/shared'
import type { FormEvent, JSX } from 'react'

/**
 * Who may reach which customers.
 *
 * The last thing in this product that could only be done with `curl` — and the
 * one whose audit event, `access-change` with `before` and `after`, exists
 * specifically to answer "who widened whose access". Doing it by hand meant the
 * question was answerable and the act was not reviewable.
 *
 * ## Nothing here decides anything
 *
 * `authorize` decides, on every route. This is a form over an endpoint that
 * already exists and already audits. The capability list beside each role comes
 * from `capabilitiesFor` in `shared` — the same function the API's own
 * authorization uses, so what the screen promises and what the API grants cannot
 * drift.
 *
 * ## Why the before and after are shown before submitting
 *
 * The audit event records both. Showing them at the point of decision means the
 * person granting sees what the trail will say — and, more to the point, sees
 * that they are *widening* rather than merely setting. A form that showed only
 * the new value would make a widening indistinguishable from a correction.
 *
 * ## Why break-glass appears but cannot be touched
 *
 * `QIM_BREAK_GLASS_EMAILS` is configuration set by a reviewed deploy,
 * deliberately not data, so that a compromised access store cannot mint one.
 * Those accounts have no record in this store at all. Listing them read-only
 * says where that boundary is; omitting them would imply they do not exist, and
 * offering to edit them would misrepresent what this screen controls.
 */

const describeScope = (scope: Assignment['scope']): string =>
  scope.kind === 'all'
    ? 'All customers'
    : scope.tenantIds.length === 0
      ? 'No customers'
      : scope.tenantIds.join(', ')

const Capabilities = ({ role }: { readonly role: string }): JSX.Element | null => {
  if (!isRole(role)) return null
  return (
    <ul className="capabilities">
      {capabilitiesFor(role).map((capability) => (
        <li key={capability}>
          <code>{capability}</code>
        </li>
      ))}
    </ul>
  )
}

export const Access = ({ baseUrl }: { readonly baseUrl: string }): JSX.Element => {
  const [assignments, setAssignments] = useState<readonly Assignment[]>([])
  const [breakGlass, setBreakGlass] = useState<readonly string[]>([])
  const [loading, setLoading] = useState(true)
  const [failure, setFailure] = useState<string | undefined>(undefined)

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('sales-cs')
  const [scopeKind, setScopeKind] = useState<'all' | 'tenants'>('tenants')
  const [tenantIds, setTenantIds] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const list = await fetchAccess(baseUrl)
      setAssignments(list.assignments)
      setBreakGlass(list.breakGlassEmails)
      setFailure(undefined)
    } catch {
      setFailure('Could not read the access list just now.')
    } finally {
      setLoading(false)
    }
  }, [baseUrl])

  useEffect(() => {
    void load()
  }, [load])

  const subject = email.trim().toLowerCase()
  const existing = assignments.find((a) => a.email === subject && a.revokedAt === undefined)
  const ids = tenantIds
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id !== '')

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    setBusy(true)
    setFailure(undefined)
    try {
      await putAssignment(baseUrl, {
        email: subject,
        role,
        scopeKind,
        ...(scopeKind === 'tenants' ? { tenantIds: ids } : {}),
      })
      setEmail('')
      setTenantIds('')
      await load()
    } catch (error: unknown) {
      // The API's rejection reasons are specific and worth passing through: a
      // scope wider than the granter's, or a self-assignment, are different
      // mistakes from a malformed request.
      //
      // It said it passed them through and did not: it printed the transport
      // message, "Request failed (400)", and never read the body. The detail
      // wins where there is one (IM-150's kind refusal names the rule), then
      // the reason code.
      const body =
        error instanceof ApiError
          ? (error.body as { error?: unknown; detail?: unknown } | undefined)
          : undefined
      const said =
        typeof body?.detail === 'string'
          ? body.detail
          : typeof body?.error === 'string'
            ? body.error
            : error instanceof ApiError
              ? error.message
              : undefined
      setFailure(
        said === undefined
          ? 'Could not set that assignment just now.'
          : `The control plane refused that: ${said}`,
      )
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (target: string): Promise<void> => {
    setBusy(true)
    setFailure(undefined)
    try {
      await revokeAssignment(baseUrl, target)
      await load()
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 409
          ? `That cannot be revoked here: ${String(error.message)}.`
          : 'Could not revoke that assignment just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <section className="panel">
        <h2>Access</h2>
        <p className="muted">
          Who may reach which customers. Every change here is recorded with both the previous and
          the new grant.
        </p>
        {failure === undefined ? null : <p className="failure">{failure}</p>}

        {loading ? (
          <p role="status">Reading…</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Role</th>
                  <th scope="col">Customers</th>
                  <th scope="col">Last changed</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <tr key={assignment.email}>
                    <td>{assignment.email}</td>
                    <td>
                      {assignment.revokedAt === undefined ? (
                        <>
                          {assignment.role}
                          <details>
                            <summary className="muted">what this grants</summary>
                            <Capabilities role={assignment.role} />
                          </details>
                        </>
                      ) : (
                        // The record stays after a revocation, so the trail is
                        // visible here and not only in the audit log.
                        <span className="muted">
                          Revoked{' '}
                          <time dateTime={assignment.revokedAt}>
                            {formatInstant(assignment.revokedAt)}
                          </time>
                        </span>
                      )}
                    </td>
                    <td>
                      {assignment.revokedAt === undefined ? describeScope(assignment.scope) : '—'}
                    </td>
                    <td>
                      {assignment.updatedAt}
                      <p className="muted">by {assignment.updatedBy}</p>
                    </td>
                    <td>
                      {assignment.revokedAt === undefined ? (
                        <button
                          type="button"
                          className="danger"
                          onClick={() => void revoke(assignment.email)}
                          disabled={busy}
                        >
                          Revoke
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <h3>Grant or change access</h3>
        <form onSubmit={(event) => void submit(event)}>
          <div className="field">
            <label htmlFor="accessEmail">Person</label>
            <input
              id="accessEmail"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="someone@qrvey.com"
            />
          </div>
          <div className="field">
            <label htmlFor="accessRole">Role</label>
            <select
              id="accessRole"
              value={role}
              onChange={(event) => setRole(event.target.value as Role)}
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <details open>
            <summary>What {role} may do</summary>
            <Capabilities role={role} />
          </details>
          <div className="field">
            <label htmlFor="accessScope">Customers</label>
            <select
              id="accessScope"
              value={scopeKind}
              onChange={(event) => setScopeKind(event.target.value === 'all' ? 'all' : 'tenants')}
            >
              <option value="tenants">Only these customers</option>
              <option value="all">All customers</option>
            </select>
          </div>
          {scopeKind === 'tenants' ? (
            <div className="field">
              <label htmlFor="accessTenants">Customer ids</label>
              <input
                id="accessTenants"
                value={tenantIds}
                onChange={(event) => setTenantIds(event.target.value)}
                placeholder="acme, acme-test"
                aria-describedby="accessTenants-hint"
              />
              <span id="accessTenants-hint" className="hint">
                Comma-separated. Empty means no customers.
              </span>
            </div>
          ) : null}

          {subject === '' ? null : (
            <div className="preview">
              {/* What the audit event will say, shown at the point of decision.
                A form that showed only the new value would make a widening
                indistinguishable from a correction. */}
              <h4>What will be recorded</h4>
              <dl className="detail">
                <dt>Before</dt>
                <dd>
                  {existing === undefined ? (
                    <span className="muted">No access</span>
                  ) : (
                    `${existing.role} — ${describeScope(existing.scope)}`
                  )}
                </dd>
                <dt>After</dt>
                <dd>
                  {role} —{' '}
                  {scopeKind === 'all'
                    ? 'All customers'
                    : describeScope({ kind: 'tenants', tenantIds: ids })}
                </dd>
              </dl>
            </div>
          )}

          <button type="submit" disabled={busy || subject === ''}>
            {busy ? 'Saving…' : existing === undefined ? 'Grant access' : 'Change access'}
          </button>
        </form>
      </section>

      {breakGlass.length === 0 ? null : (
        <section className="panel">
          <h3>Break-glass accounts</h3>
          <p className="muted">
            These are configuration, set by a reviewed deploy, and deliberately not data — so a
            compromised access store cannot create one. They have no assignment here and cannot be
            granted, changed or revoked from this screen. Use is alerted by email and Slack, and
            every decision they make is marked in the audit trail.
          </p>
          <ul>
            {breakGlass.map((address) => (
              <li key={address}>{address}</li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}
